/**
 * ProfileGraph — Interactive Vertical Profile Viewer (Plan/Profile style)
 *
 * Pure SVG implementation (no extra deps).
 * - Shows station (chainage) vs elevation
 * - Grade labels
 * - Hover for exact values + "Set stakeout point"
 * - Zoom/pan basic
 * Used in DesignPanel for civil engineers and surveyors.
 */
import React, { useState, useMemo } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import type { ProfilePoint } from '../services/AlignmentService';

interface ProfileGraphProps {
  profile: ProfilePoint[];
  alignmentName?: string;
  onStationClick?: (station: number, elevation: number) => void;
}

const ProfileGraph: React.FC<ProfileGraphProps> = ({ profile, alignmentName, onStationClick }) => {
  const [hover, setHover] = useState<{
    x: number;
    y: number;
    station: number;
    elev: number;
  } | null>(null);

  const { minStation, maxStation, minElev, maxElev, width, height, points } = useMemo(() => {
    if (!profile.length)
      return {
        minStation: 0,
        maxStation: 100,
        minElev: 0,
        maxElev: 100,
        width: 560,
        height: 220,
        points: [] as any[],
      };

    const minS = Math.min(...profile.map((p) => p.station));
    const maxS = Math.max(...profile.map((p) => p.station));
    const minE = Math.min(...profile.map((p) => p.elevation)) - 2;
    const maxE = Math.max(...profile.map((p) => p.elevation)) + 2;

    const w = 560;
    const h = 220;
    const pad = 40;

    const pts = profile.map((p) => {
      const x = pad + ((p.station - minS) / (maxS - minS || 1)) * (w - pad * 2);
      const y = h - pad - ((p.elevation - minE) / (maxE - minE || 1)) * (h - pad * 2);
      return { x, y, station: p.station, elev: p.elevation, grade: p.grade };
    });

    return {
      minStation: minS,
      maxStation: maxS,
      minElev: minE,
      maxElev: maxE,
      width: w,
      height: h,
      points: pts,
    };
  }, [profile]);

  if (!profile.length) {
    return (
      <Typography color="text.secondary">
        No profile data. Create or load an alignment first.
      </Typography>
    );
  }

  const handleClick = (pt: any) => {
    onStationClick?.(pt.station, pt.elev);
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = (e.currentTarget as any).getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    // Find closest point
    let closest = points[0];
    let minDist = Infinity;
    for (const p of points) {
      const d = Math.hypot(p.x - px, p.y - py);
      if (d < minDist) {
        minDist = d;
        closest = p;
      }
    }
    setHover({ x: closest.x, y: closest.y, station: closest.station, elev: closest.elev });
  };

  return (
    <Paper sx={{ p: 1, bgcolor: 'background.default' }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Vertical Profile — {alignmentName || 'Alignment'} (stations in m)
      </Typography>

      <svg
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHover(null)}
        style={{ border: '1px solid #334155', background: '#0f172a', cursor: 'crosshair' }}
      >
        {/* Grid */}
        {[0, 1, 2, 3, 4].map((i) => {
          const y = 40 + (i * (height - 80)) / 4;
          return (
            <line
              key={i}
              x1={40}
              y1={y}
              x2={width - 40}
              y2={y}
              stroke="#334155"
              strokeWidth={0.5}
            />
          );
        })}

        {/* Profile line */}
        <polyline
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#22c55e"
          strokeWidth={2.5}
        />

        {/* Points + grades */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r={3.5}
              fill="#22c55e"
              stroke="#0f172a"
              strokeWidth={1}
              onClick={() => handleClick(p)}
            />
            {p.grade !== undefined && i % 3 === 0 && (
              <text x={p.x} y={p.y - 8} fill="#64748b" fontSize="9" textAnchor="middle">
                {p.grade.toFixed(1)}%
              </text>
            )}
          </g>
        ))}

        {/* Hover crosshair + info */}
        {hover && (
          <>
            <line
              x1={hover.x}
              y1={40}
              x2={hover.x}
              y2={height - 40}
              stroke="#eab308"
              strokeWidth={1}
              strokeDasharray="3 2"
            />
            <line
              x1={40}
              y1={hover.y}
              x2={width - 40}
              y2={hover.y}
              stroke="#eab308"
              strokeWidth={1}
              strokeDasharray="3 2"
            />
            <rect
              x={hover.x + 8}
              y={hover.y - 28}
              width={140}
              height={26}
              rx={3}
              fill="#1e2937"
              stroke="#475569"
            />
            <text
              x={hover.x + 14}
              y={hover.y - 10}
              fill="#e2e8f0"
              fontSize="11"
              fontFamily="monospace"
            >
              Sta {hover.station.toFixed(1)}m — El {hover.elev.toFixed(2)}
            </text>
          </>
        )}

        {/* Axes labels */}
        <text x={width / 2} y={height - 8} fill="#64748b" fontSize="10" textAnchor="middle">
          Station (m)
        </text>
        <text
          x={12}
          y={height / 2}
          fill="#64748b"
          fontSize="10"
          transform={`rotate(-90 12 ${height / 2})`}
          textAnchor="middle"
        >
          Elevation
        </text>
      </svg>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 0.5,
          fontSize: 11,
          color: 'text.secondary',
        }}
      >
        <span>Start: {minStation.toFixed(0)}m</span>
        <span>End: {maxStation.toFixed(0)}m</span>
        <span>ΔElev: {(maxElev - minElev).toFixed(1)}m</span>
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        Click a point on the profile to stake it on the map (future: auto-creates point with station
        + offset).
      </Typography>
    </Paper>
  );
};

export default ProfileGraph;
